import { getSchoolSortByKey } from "state/school_money/selectors";
import { floatToMoney } from "lib/money";

/**
 * @returns {Immutable.Map}
 */
export function getStudentByID(state, studentID) {
  return state.getIn(["school_money", "students", studentID]);
}

/**
 * @returns {string}
 */
export function getStudentSchoolID(state, studentID) {
  return getStudentByID(state, studentID).get("schoolID");
}

/**
 * @returns {Immutable.List}
 */
export function getStudentList(state) {
  return state.getIn(["school_money", "students"])
    .toList()
    // sort primarily by school, then by student name within that
    // (counter-intuitive that we have to do the second part first)
    .sortBy(student => getStudentSortByKey(state, student.id))
    .sortBy(student => getSchoolSortByKey(state, student.schoolID));
}

/**
 * @returns {Immutable.List}
 */
export function getStudentIDList(state) {
  const studentList = getStudentList(state);
  return studentList.map(student => student.get("id"));
}

/**
 * @returns {Immutable.List}
 */
export function getSchoolStudentIDList(state, schoolID) {
  const studentList = getStudentList(state);
  return studentList
    .filter(student => student.get("schoolID") === schoolID)
    .map(student => student.get("id"));
}

/**
 * @returns {boolean}
 */
export function doesStudentExist(state, studentID) {
  return state.getIn(["school_money", "students"]).has(studentID);
}

/**
 * @returns {number}
 */
export function getStudentCurrentCreditAmount(state, studentID, type) {
  // if there is no credit for the gievn record it's probably because they don't have any
  return getStudentByID(state, studentID).get("credits").get(type, 0);
}

/**
 * @returns {number}
 */
export function getStudentTotalCreditAmountToPay(state, studentID, type) {
  const overdueType = type === "dinner_money" ? "dinner_money_overdue" : "club_extended_services_overdue";
  return getStudentCurrentCreditAmount(state, studentID, type) + getStudentCurrentCreditAmount(state, studentID, overdueType);
}

/**
 * @returns {number}
 */
export function getStudentTotalCreditAmount(state, studentID, type) {
  const offlineClubExtendedServicesOverdue = type === "club_extended_services" ? getStudentByID(state, studentID).get("credits").get("offline_club_extended_services_overdue", 0) : 0;
  return getStudentByID(state, studentID).get("credits").get(type, 0) + offlineClubExtendedServicesOverdue;
}

/**
 * @returns {string}
 */
export function getLastStudentID(state) {
  const studentIDList = getStudentIDList(state);
  const lastStudentID = state.getIn(["school_money", "lastStudentID"]);
  // fall back to the first student if lastStudentID is not set
  return lastStudentID || studentIDList.first();
}

/**
 * @returns {boolean}
 */
export function isStudentsReady(state) {
  return !state.getIn(["school_money", "isStudentsLoading"]);
}

/**
 * @returns {number}
 */
export function getStudentRemoteParentID(state, studentID) {
  const student = getStudentByID(state, studentID);
  return student.appserverReferenceEntityID;
}

/**
 * Get the key by which we should sort students.
 * @returns {string}
 */
export function getStudentSortByKey(state, studentID) {
  const student = getStudentByID(state, studentID);
  if (student) {
    return `${student.firstName} ${student.lastName}`;
  }
}

/**
  * @returns {boolean}
*/
export function getStudentTopUp(state, studentID, type){
  return getStudentByID(state, studentID).get("top_up").get(type, 0);
}

/**
  * @returns {boolean}
*/
export function canStudentDoDinnerTopUp(state, studentID){
  return getStudentTopUp(state, studentID, "enable_dinner_top_up")
}

/**
  * @returns {boolean}
*/
export function canStudentDoClubTopUp(state, studentID){
  return getStudentTopUp(state, studentID, "enable_club_top_up")
}

/**
  * @returns {Immutable.Map}
*/
export function getStudentExternalCashless(state, studentID, type){
  return getStudentByID(state, studentID).get("external_cashless").get(type, 0);
}

/**
   @returns {number}
*/
export function getStudentExternalDinnerBalance(state, studentID){
  var externalCashlessBalance = getStudentExternalCashless(state, studentID, "dinner_balance");
  return floatToMoney(externalCashlessBalance);
}

/**
   @returns {boolean}
*/
export function doesStudentUseExternalCashless(state, studentID){
  return getStudentExternalCashless(state, studentID, "is_external");
}

/**
 * @returns {boolean}
 */
export function doesStudentHasMisId(state, studentID) {
  const studentMisId = getStudentByID(state, studentID).get("misId");
  return (studentMisId !== undefined && studentMisId !== "");
}

/**
 * @returns {string}
 */
export function getServerIDForStudent(state, studentID) {
  const students = getStudentList(state);
  const student = students && students.find(student => student.get("remoteID") === Number(studentID));
  return student ? String(student.get("serverID")) : undefined;
}



// WEBPACK FOOTER //
// ./src/state/school_money/selectors/students.js